import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import _ from 'lodash';
import classes from './tabs.module.scss';
import api from '../../../../../api/api';
import utils from '../../../../../utils/utils';
import accountHelpers from '../../../../../utils/accountHelpers';
import InputField from '../../../../_common/Form/TextFields/InputField';
import Checkbox from '../../../../_common/Form/Checkbox/Checkbox';
import Button from '../../../../_common/Form/Buttons/Button';
import Loader from '../../../../_common/Loader/Loader';
import AttentionModal from '../../../../_common/Modal/AttentionModal/AttentionModal';

const getDefaultMessage = (lng, patient) => {
  const patientName = `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim();

  const messages = {
    en: `Hi ${patientName},
You have a prescription ready to be scheduled for delivery.
Please contact us at +1 (877) 570-7787.
Thank you,
SortPak.
To opt out of future text messages, reply STOP at any time.`,

    es: `Hola ${patientName}:
Tiene una receta médica lista para programar su entrega.
Por favor, comuníquese con nosotros al +1 (877) 570-7787.
Gracias,
SortPak.
Si desea dejar de recibir mensajes de texto, responda STOP en cualquier momento.`,

    optMessage: `Hi ${patientName},
This is SortPak Pharmacy!
Would you like to receive text message updates about your prescriptions, delivery scheduling, and other important pharmacy notifications?
Reply YES to opt in and receive SMS updates from SortPak.
Message and data rates may apply. Message frequency varies. You can opt out at any time by replying STOP.
Thank you,
SortPak Pharmacy.
+1 (877) 570-7787`,
  };

  return messages[lng] || '';
};

const NotifyPatient = ({ info, changeScriptInfo }) => {
  const { PatientId, Patient, status } = info;
  const isSmsOptOut = Patient?.smsOptOut === true;
  const hasActivatePermission = accountHelpers.isInOneOfRoles(['Admin', 'Pharmacy']);

  const { scriptId } = useParams();

  const [data, setData] = useState({
    message: '',
    rxScheduleTemplateEn: false,
    rxScheduleTemplateEs: false,
    optMessage: false,
    sendPhone: !isSmsOptOut,
    sendEmail: !isSmsOptOut,
    patientPhone: Patient?.phone || '',
    patientPhone2: Patient?.phone2 || '',
    patientPhone3: Patient?.phone3 || '',
    patientEmail: Patient?.email || '',
  });
  const [errors, setErrors] = useState({});
  const [sendLoading, sendLoadingToggle] = useState(false);
  const [smsOptOutLoading, smsOptOutLoadingToggle] = useState(false);
  const [smsOptOutModalOpen, smsOptOutModalToggle] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, historyLoadingToggle] = useState(true);

  const getHistory = useCallback(async () => {
    try {
      historyLoadingToggle(true);

      const { data: { data: historyData = [] } } = await api.getNotifyPatientHistory({ patientId: PatientId });

      setHistory(historyData);
    } catch (e) {
      console.warn(e);

      setHistory([]);
    }

    historyLoadingToggle(false);
  }, [scriptId]);

  useEffect(() => {
    getHistory();
  }, [scriptId]);

  const changeData = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const changeRXScheduleTemplate = (lng, checked) => {
    setData((prev) => ({
      ...prev,
      rxScheduleTemplateEn: lng === 'en' ? checked : false,
      rxScheduleTemplateEs: lng === 'es' ? checked : false,
      optMessage: lng === 'optMessage' ? checked : false,
      message: checked ? getDefaultMessage(lng, Patient) : '',
    }));

    setErrors((prev) => ({ ...prev, message: '' }));
  };

  const send = async () => {
    if (isSmsOptOut) return;

    const message = data.message.trim();
    const patientPhone = data.patientPhone.trim();
    const patientPhone2 = data.patientPhone2.trim();
    const patientPhone3 = data.patientPhone3.trim();
    const patientEmail = data.patientEmail.trim();

    const newErrors = {};

    if (!data.sendPhone && !data.sendEmail) {
      toast.error('Please select SMS or Email');

      return;
    }

    if (!message) {
      newErrors.message = 'Please write message';
    }

    if (data.sendPhone && !(patientPhone || patientPhone2 || patientPhone3)) {
      newErrors.patientPhone = 'Please enter phone';
    }

    if (data.sendEmail) {
      if (!patientEmail) {
        newErrors.patientEmail = 'Please enter email';
      } else if (!utils.isEmail(patientEmail)) {
        newErrors.patientEmail = 'Please enter a valid email';
      }
    }

    if (!_.isEmpty(newErrors)) {
      setErrors(newErrors);

      toast.error('Please correct the errors');

      return;
    }

    try {
      sendLoadingToggle(true);

      await api.notifyPatient({
        message,
        patientId: PatientId,
        patientEmail,
        patientPhone: _.compact([patientPhone, patientPhone2, patientPhone3]),
        sendEmail: data.sendEmail,
        sendPhone: data.sendPhone,
        scriptId,
      });

      await getHistory();

      toast.success('Sent successfully');

      setData((prev) => ({
        ...prev,
        rxScheduleTemplateEn: false,
        rxScheduleTemplateEs: false,
        optMessage: false,
        message: '',
      }));
    } catch (e) {
      console.warn(e);

      toast.error(e.response?.data?.message || 'Something went wrong');
    }

    sendLoadingToggle(false);
  };

  const toggleSmsOptOut = async () => {
    const value = !isSmsOptOut;

    try {
      smsOptOutLoadingToggle(true);

      await api.updatePatientSmsOptOut({ patientId: PatientId, value });

      changeScriptInfo({ Patient: { ...Patient, smsOptOut: value } });

      setData((prev) => ({ ...prev, sendPhone: !value, sendEmail: !value }));
      setErrors({});

      await getHistory();
      smsOptOutModalToggle(false);

      toast.success(value ? 'Notifications disabled successfully' : 'Notifications activated successfully');
    } catch (e) {
      console.warn(e);

      toast.error(e.response?.data?.message || 'Something went wrong');
    }

    smsOptOutLoadingToggle(false);
  };

  return (
    <div className={classes.notify_patient_wrapper}>
      <div
        className={`${classes.notify_box} ${isSmsOptOut ? classes.notify_box_disabled : ''}`}
        data-tooltip-id={isSmsOptOut ? 'default_id' : undefined}
        data-tooltip-content="The patient does not wish to receive notifications"
      >
        <div className={classes.notify_header}>
          Notify Patient

          <span>{`${Patient?.firstName || ''} ${Patient?.lastName || ''}`}</span>

          {hasActivatePermission && (
            <div
              className={classes.notify_activate}
              data-tooltip-id="default_id"
              data-tooltip-hidden
            >
              <Button
                title={isSmsOptOut ? 'Activate' : 'Disable'}
                btnClassName={classes.notify_activate_btn}
                buttonColor={!isSmsOptOut ? '#6f6868' : undefined}
                reverseColor
                onClick={() => smsOptOutModalToggle(true)}
              />
            </div>
          )}
        </div>

        <div className={classes.notify_body}>
          <div className={classes.notify_row}>
            <Checkbox
              checked={data.sendPhone}
              disabled={isSmsOptOut}
              onChange={(checked) => {
                changeData('sendPhone', checked);

                setErrors((prev) => ({ ...prev, type: '', patientPhone: '' }));
              }}
            />

            <InputField
              label="Phone"
              height={45}
              labelDefaultPosition={10}
              value={data.patientPhone}
              disabled={isSmsOptOut || !data.sendPhone}
              onChange={(v) => changeData('patientPhone', v)}
              error={errors.patientPhone}
            />

            <InputField
              label="Phone"
              height={45}
              labelDefaultPosition={10}
              value={data.patientPhone2}
              disabled={isSmsOptOut || !data.sendPhone}
              onChange={(v) => changeData('patientPhone2', v)}
              error={errors.patientPhone2}
            />

            <InputField
              label="Phone"
              height={45}
              labelDefaultPosition={10}
              value={data.patientPhone3}
              disabled={isSmsOptOut || !data.sendPhone}
              onChange={(v) => changeData('patientPhone3', v)}
              error={errors.patientPhone3}
            />
          </div>

          <div className={classes.notify_row}>
            <Checkbox
              checked={data.sendEmail}
              disabled={isSmsOptOut}
              onChange={(checked) => {
                changeData('sendEmail', checked);

                setErrors((prev) => ({ ...prev, type: '', patientEmail: '' }));
              }}
            />

            <InputField
              label="Email"
              height={45}
              labelDefaultPosition={10}
              value={data.patientEmail}
              disabled={isSmsOptOut || !data.sendEmail}
              onChange={(v) => changeData('patientEmail', v)}
              error={errors.patientEmail}
            />
          </div>

          <InputField
            label="Message"
            textareaField
            height={260}
            value={data.message}
            disabled={isSmsOptOut}
            onChange={(v) => changeData('message', v)}
            error={errors.message}
          />

          <div className={classes.notify_footer}>
            <div className={classes.notify_templates}>
              <Checkbox
                label="Rx Schedule Template (EN)"
                checked={data.rxScheduleTemplateEn}
                disabled={isSmsOptOut}
                onChange={(checked) => changeRXScheduleTemplate('en', checked)}
              />

              <Checkbox
                label="Rx Schedule Template (ES)"
                checked={data.rxScheduleTemplateEs}
                disabled={isSmsOptOut}
                onChange={(checked) => changeRXScheduleTemplate('es', checked)}
              />

              {status === 'Schedule' && (
                <Checkbox
                  label="OPT-in message"
                  checked={data.optMessage}
                  disabled={isSmsOptOut}
                  onChange={(checked) => changeRXScheduleTemplate('optMessage', checked)}
                />
              )}
            </div>

            <div className={classes.notify_btn_wrapper}>
              <Button
                title="Send"
                sendButton
                reverseColor
                loading={sendLoading}
                disabled={isSmsOptOut}
                onClick={send}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={classes.notify_history}>
        <div className={classes.notify_history_title}>Sent History</div>

        {historyLoading
          ? (
            <div className={classes.notify_history_loader}>
              <Loader />
            </div>
          )

          : !history.length
            ? <div className={classes.notify_history_empty}>No Data Found</div>

            : history.map((h) => {
              const channels = [];

              if (h.sentViaPhone) channels.push('SMS');
              if (h.sentViaEmail) channels.push('Email');

              return (
                <div
                  key={`${h.createdAt}_${h.sentByUserId}_${h.message}`}
                  className={classes.notify_history_item}
                >
                  <div className={classes.notify_history_header}>
                    <span>{h.sentByUserName || ''}</span>

                    <span>{moment.utc(h.createdAt).local().format('MM/DD/YYYY  LT')}</span>
                  </div>

                  <div className={classes.notify_history_text}>
                    {!!channels.length && <div>Sent via</div>}

                    <div className={classes.notify_history_channel}>{channels.join(', ')}</div>
                  </div>

                  {!!h.message && <div className={classes.notify_history_message}>{h.message}</div>}
                </div>
              );
            })}
      </div>

      <AttentionModal
        isOpen={smsOptOutModalOpen}
        onClose={() => smsOptOutModalToggle(false)}
        onSave={toggleSmsOptOut}
        title={isSmsOptOut ? 'Activate Notifications?' : 'Disable Notifications?'}
        text={`Are you sure you want to ${isSmsOptOut ? 'activate' : 'disable'} notifications for this patient?`}
        loading={smsOptOutLoading}
      />
    </div>
  );
};

NotifyPatient.propTypes = {
  info: PropTypes.object.isRequired,
  changeScriptInfo: PropTypes.func.isRequired,
};

export default NotifyPatient;
